import React, { useCallback, useEffect, useRef, useState } from "react";

import axios from "axios";
import queryString from "query-string";
import { connect } from "react-redux";
import { styled } from "styled-components";

import { gridSpacing, makeHumanReadable, cachedAxios } from "@utils";

import CustomOptionsPopover from "./CustomOptionsPopover";
import FilterButton from "./FilterButton";
import type { FilterOptionProps } from "./FilterOptionProps";
import Options from "./Options";
import SaveFilterModal from "./SavedFiltersModal";
import { describeQueryFallback } from "./SavedFiltersModal/describeQuery";
import type {
  FilterDescription,
  SavedView,
  SavedViewLayout,
} from "./SavedFiltersModal/FilterModalProps";
import { setAdvancedFilterRefresh } from "../../actions/main";
import Button from "../Button/Button";
import SearchText from "../SearchText/SearchText";
import Select from "../Select/Select";
import type { OptionProp } from "../Select/types";
import type {
  AdvancedFilterFilter as AdvancedFilterFilterAPIProps,
  AdvancedFilter as SearchAdvancedFilterAPIProps,
} from "~/src/types";

const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${gridSpacing[2]}px;
  align-items: center;
  min-height: 28px; // This is to prevent the table from jolting when filters are loaded in
`;

type DataVizFilterItemProps = {
  key: string;
  label: string;
  name: string;
  options: string[];
  chartTitles?: string[];
};

type DataVizFilterOptionsProps = Record<string, DataVizFilterItemProps[]>;

export type AdvancedFiltersProps = {
  app?: string;
  urlPattern?: string;
  pageKey?: string;
  query?: string;
  setQuery?: (query: string, queryDotNotation: string) => void;
  setQueryInURL?: boolean;
  hideSavedFilters?: boolean;
  primaryFilters?: string[];
  hiddenFilters?: string[];
  hiddenGroups?: string[];
  showNullInFilters?: string[];
  singleSelectFilters?: string[];
  disabledFilters?: string[];
  constantBaseFilters?: Record<string, string>;
  baseFilters?: Record<string, string>;
  quickFilters?: {
    name: string;
    filters: Record<string, string[]>;
  }[];
  incomingFilters?: FilterOptionProps[];
  dataVizFilters?: DataVizFilterOptionsProps;
  model?: string;
  noFilters?: boolean;
  inlineViewButtons?: boolean;
  refresh?: boolean;
  setAdvancedFilterRefresh?: (refresh: boolean) => void;
  searchTextWidth?: number | string;
  searchTextHidden?: boolean;
  loadingCallback?: (loading: boolean) => void;
  loading?: boolean;
  tableLoading?: boolean; // This is to have tables load in with the filters on first load
  ignoreNonExistentFilterErrors?: boolean;
  /** Lets saved views also save and restore the table's layout */
  tableLayout?: SavedViewLayout;
};

// The query is built with dot notation so we know what group the filter is part of - this can be removed though so the
// table using the query can send the correct query to the API which does not use dot notation
export const buildQuery = (
  _filters: Record<string, string[]>,
  removeDotNotation = false,
) => {
  if (!_filters) return "";

  let query = "?";
  Object.keys(_filters).forEach((key: string) => {
    let newKey = key;
    if (removeDotNotation) {
      const splitKey = key.split(".");
      newKey = splitKey[splitKey.length - 1];
    }

    if (_filters[key]?.length > 0) {
      if (Array.isArray(_filters[key])) {
        _filters[key].forEach((filter) => {
          query += `${newKey}=${filter.replaceAll("+", "%2B")}&`;
        });
      } else {
        query += `${newKey}=${_filters[key]}&`;
      }
    }
  });

  return query.substring(0, query.length - 1);
};

const transformDataVizFilterOptions = (
  filterOptions: DataVizFilterOptionsProps,
) => {
  const advFilterOptions: FilterOptionProps[] = [];
  Object.keys(filterOptions).forEach((key) => {
    const currentFilterOptions: FilterOptionProps[] = [];

    filterOptions[key].forEach((groupOption) => {
      currentFilterOptions.push({
        label: makeHumanReadable(groupOption.label),
        value: groupOption.label,
        options: groupOption.options
          ?.filter((o) => !!o)
          .map((o) => {
            return {
              value: o,
              label: o,
              options: undefined,
            };
          }),
      });
    });

    advFilterOptions.push({
      label: makeHumanReadable(key),
      value: key,
      options: currentFilterOptions,
    });
  });

  return advFilterOptions;
};

/**
 * Advanced Filters
 *
 * @component
 * @param {string} app - App used for the API for saved filters
 * @param {string} urlPattern - URL used for the API for advanced filters
 * @param {string} pageKey - pageKey used in case of multiple advanced filters using the same app and url pattern so they do not conflict per page
 * @param {string} query - Incoming query string from the URL - this will override the URL query string
 * @param {function} setQuery - The function to set the query string in the component using advanced filters
 * @param {boolean} setQueryInURL - Determines whether to set the query string in the URL or control it from the component
 * @param {boolean} hideSavedFilters - Determines whether to hide the saved filters button
 * @param {string[]} primaryFilters - The filters that will always be displayed wether they have a value or not
 * @param {string[]} hiddenFilters - Filters that will be hidden from the user
 * @param {string[]} hiddenGroups - Groups that will be hidden from the user
 * @param {Record<string, string>} constantBaseFilters - Constant filters that will always be added to the query
 * @param {Record<string, string>} baseFilters - The filters the component will mount with however the user can remove or change these
 * @param {name: string; filters: Record<string, string[]>[]} - Filters that the user can click a button and it will filter by whatever is in the quick filter object
 * @param {FilterOptionProps} incomingFilters - Incoming filters instead of loading them from the API advance search
 * @param {DataVizFilterOptionsProps} dataVizFilters - Filters for data viz
 * @param {string} model - Model to be used along with incomingFilters to save the filters
 * @param {boolean} noFilters - If true the component will not load any filters
 * @param {boolean} inlineViewButtons - If true the view buttons will be inline with the filter buttons (this is originally used for data viz)
 * @param {boolean} refresh - If true the component will refresh the filters
 * @param {function} setAdvancedFilterRefresh - The function to set the refresh state of the filters
 * @param {number} searchTextWidth - The width of the search text
 * @param {boolean} searchTextHidden - Determines weather the search text is hidden (default true)
 * @param {function} loadingCallback - Callback when loading changes when loading filters
 * @param {boolean} loading - External loading state that overrides the internal one when provided
 * @returns {JSX.Element} The rendered AdvancedFilters component.
 */
const AdvancedFilters = ({
  app,
  pageKey = "",
  urlPattern,
  query,
  setQuery = () => {},
  setQueryInURL = true,
  hideSavedFilters = false,
  primaryFilters,
  quickFilters,
  hiddenFilters,
  hiddenGroups,
  showNullInFilters,
  singleSelectFilters,
  disabledFilters,
  constantBaseFilters,
  baseFilters,
  incomingFilters,
  dataVizFilters,
  model = "",
  noFilters,
  inlineViewButtons,
  refresh,
  searchTextWidth,
  searchTextHidden,
  setAdvancedFilterRefresh = () => {},
  loadingCallback,
  loading: externalLoading,
  tableLoading,
  ignoreNonExistentFilterErrors = false,
  tableLayout,
}: AdvancedFiltersProps) => {
  const [currentModel, setCurrentModel] = useState(model);
  const [clearedFilters, setFiltersCleared] = useState(false);
  const [filterData, setFilterData] = useState<FilterOptionProps[]>(
    incomingFilters ?? [],
  );
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [savedFilters, setSavedFilters] = useState<SavedView[]>([]);
  const [secondaryFilters, setSecondaryFilters] = useState<string[]>([]);
  const [primarySearchFilter, setPrimarySearchFilter] = useState("");
  const [internalLoading, setInternalLoading] = useState(true);
  const [filterModalOpened, setFilterModalOpened] = useState<
    "newFilter" | boolean
  >(false);
  const [newFilter, setNewFilter] = useState<FilterOptionProps | null>(null);
  const [usingSavedFilterName, setUsingSavedFilterName] = useState("");
  // Layout right after a view was applied; a later change means the view was adjusted, so it can be saved as new
  const viewLayout = useRef<string | null>(null);
  const showView = (name: string) => {
    setUsingSavedFilterName(name);
    viewLayout.current = null;
  };
  // A view from the URL applies its layout once, not again when the filters reload
  const appliedUrlView = useRef<string | null>(null);
  const [closeMenu, setCloseMenu] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [firstLoad, setFirstLoad] = useState(true);
  const [tableFirstLoaded, setTableFirstLoaded] = useState(
    !Boolean(tableLoading),
  );

  const loading =
    (!tableFirstLoaded && tableLoading === true) ||
    externalLoading === true ||
    internalLoading === true;

  const addBaseFilters = (
    queryParams = "",
    _clearedFilters = clearedFilters,
  ) => {
    if (
      !queryParams && // Only add base filters if there are no query params
      !_clearedFilters && // Only add base filters if the filters have not been cleared
      baseFilters
    ) {
      if (!queryParams) {
        queryParams = "?";
      }
      Object.keys(baseFilters).forEach((filterKey: string, i: number) => {
        if (!queryParams.includes(filterKey)) {
          queryParams += `${
            queryParams === "?" && i === 0 ? "" : "&"
          }${filterKey}=${baseFilters[filterKey as keyof typeof baseFilters]}`;
        }
      });
    }

    if (constantBaseFilters) {
      if (!queryParams) {
        queryParams = "?";
      }
      Object.keys(constantBaseFilters).forEach(
        (filterKey: string, i: number) => {
          if (!queryParams.includes(filterKey)) {
            queryParams += `${
              queryParams === "?" && i === 0 ? "" : "&"
            }${filterKey}=${
              constantBaseFilters[filterKey as keyof typeof constantBaseFilters]
            }`;
          }
        },
      );
    }

    return queryParams;
  };

  const removeConstantBaseFilters = (_filters: Record<string, string[]>) => {
    const removedBaseFilters = { ..._filters };
    Object.keys(constantBaseFilters ?? {}).forEach((filterKey: string) => {
      delete removedBaseFilters[filterKey];
    });

    return removedBaseFilters;
  };

  const getQueryParams = (_clearedFilters: boolean = false) => {
    let queryParams = "";

    if (query) {
      queryParams = query.replaceAll("%2B", "+");
    } else if (setQueryInURL) {
      queryParams = window.location.search;
    }
    if (
      (setQueryInURL &&
        (queryParams.startsWith("?version") ||
          queryParams.startsWith("?filter"))) ||
      _clearedFilters
    ) {
      queryParams = "";
    }

    queryParams = addBaseFilters(queryParams, _clearedFilters);

    return queryParams;
  };

  const buildFilters = (url: string): Record<string, string[]> => {
    const _filter: Record<string, string[]> = {};
    url.split("&").map((obj) => {
      const keyValue = obj.split("=");
      const key = keyValue?.[0]?.replace("?", "") ?? "";

      if (key) {
        if (_filter[key]) {
          _filter[key].push(keyValue[1].replaceAll("%20", " "));
        } else {
          _filter[key] = [keyValue[1].replaceAll("%20", " ")];
        }
      }
    });

    return _filter;
  };

  const buildAndSetQueryFilter = (
    _filters: Record<string, string[]>,
    addFilters = false,
  ) => {
    let newFilters = {};

    setUsingSavedFilterName("");

    if (addFilters) {
      newFilters = { ...filters, ..._filters };
    } else {
      newFilters = _filters;
    }

    const query = buildQuery(newFilters);
    const queryToSend = buildQuery(newFilters, true);

    setQuery(queryToSend, query);
    setFilters(newFilters);

    if (query) {
      setQueryInURL &&
        window.history.pushState(null, "", `${query}${window.location.hash}`);
    } else {
      setQueryInURL &&
        window.history.replaceState(
          null,
          "",
          `${window.location.pathname}${window.location.hash}`,
        );
    }
  };

  // Use effect being used here bc when newFilter is set via freeform we need to rebuild the query and the only way to access the latest
  // state is to use the hook
  useEffect(() => {
    // If the filter is a custom filter then we need to just close the popover because it
    // is just 1 value that is being added
    if (
      newFilter?.identifier &&
      newFilter?.type !== "MULTI_OPTION" &&
      newFilter?.type !== "SINGLE_OPTION"
    ) {
      if (
        newFilter?.type === "FREEFORM" ||
        // @ts-expect-error - This may also be a type from the API
        newFilter?.type === "DATE"
      ) {
        setCloseMenu(true);
        setSecondaryFilters((_secondaryFilters) => [
          ..._secondaryFilters,
          newFilter.identifier as string, // casting this as string because it is not undefined
        ]);
        setTimeout(() => setCloseMenu(false), 50);
        setNewFilter(null);
      }

      buildAndSetQueryFilter(
        { [newFilter.identifier]: [newFilter.value ?? ""] },
        true,
      );
    }
  }, [newFilter]);

  const clearFilters = () => {
    setQueryInURL &&
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.hash}`,
      );
    setFiltersCleared(true);
    const newQuery = getQueryParams(true);
    setQuery(newQuery, newQuery);
    setFilters(buildFilters(newQuery));
    setPrimarySearchFilter("");
    setSecondaryFilters([]);
  };

  const findParentIdentifier = (
    identifier: string,
    filterData: AdvancedFilterFilterAPIProps[] | FilterOptionProps[],
  ): string => {
    let id = identifier;

    const foundFilter = filterData.find((o) => o.identifier === id);

    if (foundFilter?.group) {
      id = `${foundFilter.group.replaceAll(" ", "_").toLowerCase()}.${id}`;
    } else {
      return id;
    }

    return findParentIdentifier(id, filterData);
  };

  const loadFilters = async () => {
    const useIncomingFilter =
      incomingFilters !== null && incomingFilters !== undefined;
    const useDataVizFilters =
      dataVizFilters !== null && dataVizFilters !== undefined;

    let loadingTimeout: NodeJS.Timeout | null = null;
    let isRequestCompleted = false;

    const startLoading = () => {
      loadingTimeout = setTimeout(() => {
        if (!isRequestCompleted) {
          setInternalLoading(true);
          loadingCallback && loadingCallback(true);
        }
      }, 250);
    };

    const stopLoading = () => {
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }
      setInternalLoading(false);
      loadingCallback && loadingCallback(false);
    };

    if (noFilters) {
      stopLoading();
      return;
    }

    startLoading();
    const params: { excluded_filter?: string[]; excluded_group?: string[] } = {
      excluded_filter: undefined,
      excluded_group: undefined,
    };

    if (hiddenFilters) {
      params.excluded_filter = hiddenFilters;
    }

    if (hiddenGroups) {
      params.excluded_group = hiddenGroups;
    }

    // Load options for advanced filters if not using incoming filters or data viz filters
    let advFilterData: SearchAdvancedFilterAPIProps = {
      app: "",
      model: "",
      search_fields: [],
      filters: [],
    };
    if (!useDataVizFilters && !useIncomingFilter && app) {
      advFilterData = await cachedAxios
        .get(`${app}${urlPattern ? `/${urlPattern}` : ""}/advanced_filters`, {
          params,
          paramsSerializer: (params) =>
            queryString.stringify(params, {
              arrayFormat: "index",
            }),
        })
        .then((res) => res.data.result)
        .catch((err) => {
          setErrors((err) => {
            return { ...err, main: "Error loading search" };
          });
          console.error("Something went wrong loading search", err);
          return { app: "", model: "", search_fields: [], filters: [] };
        });
    }

    // Load saved filters
    let searchFiltersResult = [];

    if (app) {
      searchFiltersResult = await axios
        .get("search/filters")
        .then((res) => res.data.result)
        .catch((err) => {
          setErrors((err) => {
            return { ...err, view: "Error loading saved views" };
          });

          console.error("Something went wrong loading saved views", err);
          return [];
        });
    }

    isRequestCompleted = true;

    const searchFilters = searchFiltersResult.filter(
      (o: { app: string; model: string; key: string }) =>
        o.app === app &&
        (o.model === advFilterData?.model || o.model === model) &&
        o.key === pageKey,
    );

    let newFilters: Record<string, string[]> = {
      search: [],
    };

    if (
      window.location.search.includes("filter") ||
      query?.includes("filter")
    ) {
      const slug = window.location.search.split("=")?.[1];

      const foundFilter = searchFilters.find(
        (o: { slug: string }) => o.slug === slug,
      );

      if (foundFilter) {
        newFilters = buildFilters(foundFilter.query);

        if (
          newFilters &&
          "search" in newFilters &&
          (newFilters.search as string[])[0]
        ) {
          setPrimarySearchFilter((newFilters.search as string[])[0]);
        }

        showView(foundFilter.name);
        if (
          foundFilter.table_state &&
          tableLayout &&
          appliedUrlView.current !== slug &&
          tableLayout.restoredView !== slug
        ) {
          tableLayout.apply(foundFilter.table_state, slug);
        }
        appliedUrlView.current = slug;

        const sendQuery = buildQuery(newFilters, true);
        const dotQuery = buildQuery(newFilters);
        setQuery(sendQuery, dotQuery);
      }
    } else {
      newFilters = buildFilters(getQueryParams());
    }

    const cleanFilterData = (
      _filterData: AdvancedFilterFilterAPIProps[] | FilterOptionProps[],
    ) => {
      const ungroupedFilters: FilterOptionProps[] = [];
      const groupedFilters: Record<string, FilterOptionProps[]> = {};

      const cleanOptions = (
        _options: FilterOptionProps | FilterOptionProps[] | undefined,
      ): FilterOptionProps | FilterOptionProps[] | undefined => {
        if (!_options) {
          return undefined;
        } else if (_options && Array.isArray(_options)) {
          return _options.map(cleanOptions) as FilterOptionProps[];
        }

        let customOptions = undefined;
        if (
          _options.type &&
          _options.type !== "MULTI_OPTION" &&
          _options.type !== "SINGLE_OPTION"
        ) {
          const optionProps = {
            name: _options.name ?? _options.label,
            identifier: `${_options.identifier ?? _options.value}`
              .replaceAll(`${_options.gt_suffix}`, "")
              .replaceAll(`${_options.lt_suffix}`, ""),
            type: _options.type,
            gt_suffix: _options.gt_suffix,
            lt_suffix: _options.lt_suffix,
            filters,
          };

          customOptions = (
            <Options
              {...optionProps}
              useOnBlur
              onChange={(id, e) => {
                // Only do something if the value is not empty
                if (e === "") {
                  return;
                }

                const foundFilter = _filterData.find(
                  (o) => o.identifier === _options.identifier,
                );

                if (foundFilter) {
                  let identifier = findParentIdentifier(
                    _options.identifier!,
                    _filterData,
                  );

                  // If id includes a suffix we need to readd it to the identifier
                  if (id.includes(`${foundFilter.gt_suffix}`)) {
                    identifier += foundFilter.gt_suffix;
                  } else if (id.includes(`${foundFilter.lt_suffix}`)) {
                    identifier += foundFilter.lt_suffix;
                  }

                  setNewFilter({
                    ...foundFilter,
                    custom: true,
                    options: undefined,
                    label:
                      (foundFilter as FilterOptionProps).name ??
                      (foundFilter as FilterOptionProps).label ??
                      "",
                    identifier,
                    value: e,
                  });
                }
              }}
            />
          );
        }

        // Ensure options is an array even if it DNE
        if (
          (_options.type === "MULTI_OPTION" ||
            _options.type === "SINGLE_OPTION") &&
          !_options.options
        ) {
          _options.options = [];
        }

        return {
          ..._options,
          customOptions,
          label: _options.name ?? _options.label,
          value: _options.identifier ?? _options.value,
          identifier: _options.identifier ?? _options.value,
          options: cleanOptions(_options.options as FilterOptionProps[]),
        } as FilterOptionProps;
      };

      _filterData.forEach((o) => {
        const cleanedFilter = cleanOptions(
          o as FilterOptionProps,
        ) as FilterOptionProps;
        if (o.group) {
          if (!groupedFilters[o.group]) {
            groupedFilters[o.group] = [cleanedFilter];
          } else {
            groupedFilters[o.group].push(cleanedFilter);
          }
        } else {
          ungroupedFilters.push(cleanedFilter);
        }
      });

      const _returnData = [...ungroupedFilters];
      Object.keys(groupedFilters).forEach((d) => {
        _returnData.push({
          label: d,
          value: d.replaceAll(" ", "_").toLowerCase(),
          options: groupedFilters[d],
        });
      });

      return _returnData;
    };

    if (newFilters.search?.[0]) {
      setPrimarySearchFilter(newFilters.search[0]);
    }

    if (useDataVizFilters) {
      setFilterData(transformDataVizFilterOptions(dataVizFilters));
    } else if (useIncomingFilter && incomingFilters) {
      setFilterData(cleanFilterData(incomingFilters));
    } else if (advFilterData?.filters) {
      setFilterData(cleanFilterData(advFilterData.filters));
    }

    // If the first load and the query string includes a dot (which indicates grouping) then we need to build the query filter
    // and resend the query to the component. However, this is bad UX because the table WILL refresh once the advanced filters are loaded
    if (
      firstLoad &&
      (window.location.search.includes(".") || query?.includes("."))
    ) {
      buildAndSetQueryFilter(newFilters);
      setFirstLoad(false);
    }

    setCurrentModel(model || advFilterData.model);
    setFilters(newFilters);
    setSecondaryFilters(
      Object.keys(newFilters).filter(
        (key) =>
          !primaryFilters?.includes(key) &&
          !hiddenFilters?.includes(key) &&
          !Object.keys(baseFilters || {})?.includes(key) &&
          !Object.keys(constantBaseFilters || {})?.includes(key),
      ),
    );
    setSavedFilters(searchFilters);
    stopLoading();
  };

  useEffect(() => {
    loadFilters();
    if (setQueryInURL) {
      const currentQuery = getQueryParams();
      setQuery(currentQuery, currentQuery);
    }

    if (refresh) {
      setAdvancedFilterRefresh(false);
    }
  }, [refresh, JSON.stringify(incomingFilters), dataVizFilters]);

  useEffect(() => {
    if (tableLoading === false && tableFirstLoaded === false) {
      setTableFirstLoaded(true);
    }
  }, [tableLoading]);

  const findFilter = (
    id: string,
    filters: FilterOptionProps[],
  ): FilterOptionProps | undefined => {
    let _id = id;
    let splitId: string[] = [];
    if (id.includes(".")) {
      splitId = id.split(".");
      _id = splitId[0];
    }

    const foundFilter = filters.find(
      (obj) =>
        obj.value === _id ||
        `${obj.value}${obj?.gt_suffix}` === _id ||
        `${obj.value}${obj?.lt_suffix}` === _id,
    );

    if (splitId.length > 1 && foundFilter?.options) {
      splitId.shift();
      return findFilter(splitId.join("."), foundFilter.options);
    }

    return foundFilter;
  };

  const renderFilterSelect = (
    identifier: string,
    useOnBlur: boolean = false,
  ) => {
    if (identifier === "search") {
      return null;
    }

    const filter = findFilter(identifier, filterData);
    if (!filter) {
      if (!ignoreNonExistentFilterErrors) {
        // eslint-disable-next-line no-restricted-syntax
        console.warn(`Filter with identifier ${identifier} not found`);
      }
      return null;
    }

    if (filter?.customOptions) {
      // Check to see if the custom options has multiple values in secondary filters - if it does only render one!
      if (
        identifier.includes(`${filter.gt_suffix}`) &&
        secondaryFilters.includes(
          identifier.replace(`${filter.gt_suffix}`, `${filter.lt_suffix}`),
        )
      ) {
        return null;
      }

      return (
        <CustomOptionsPopover
          key={identifier}
          useOnBlur={useOnBlur}
          data={{
            ...filter,
            identifier: `${identifier}`
              .replaceAll(`${filter.gt_suffix}`, "")
              .replaceAll(`${filter.lt_suffix}`, ""),
          }}
          filters={filters}
          onChange={(newFilters) => {
            buildAndSetQueryFilter({ ...filters, ...newFilters });
          }}
          menuClosed={() => {
            setSecondaryFilters(
              secondaryFilters.filter((o) => filters[o]?.length > 0),
            );
            setNewFilter(null);
          }}
          clearValues={() => {
            const _newFilters = { ...filters };
            delete _newFilters[`${identifier}`];
            delete _newFilters[`${identifier}${filter?.gt_suffix}`];
            delete _newFilters[`${identifier}${filter?.lt_suffix}`];
            setSecondaryFilters(
              secondaryFilters.filter((o) => o !== identifier),
            );
            buildAndSetQueryFilter(_newFilters);
          }}
        />
      );
    }

    let filterOptions = filter.options || [];

    // Hide __null__ options unless this filter is in showNullInFilters
    if (!showNullInFilters?.includes(identifier)) {
      filterOptions = filterOptions.filter((opt) => opt.value !== "__null__");
    }

    const isSingleSelect = singleSelectFilters?.includes(identifier);
    const isDisabled = disabledFilters?.includes(identifier);

    return (
      <Select
        selectedValuesAtTop
        multiValues={!isSingleSelect}
        checkbox={!isSingleSelect}
        key={identifier}
        showSelectAll={
          !isSingleSelect &&
          Boolean(filterOptions && filterOptions?.length <= 100)
        }
        options={filterOptions as OptionProp[]}
        value={filters[identifier] ?? []}
        onChange={(e) => {
          const _newFilters = { ...filters };
          if (e?.length === 0) {
            delete _newFilters[identifier];
          } else {
            // For single select, only keep the last selected value
            _newFilters[identifier] = (isSingleSelect ? [e] : e) as string[];
          }

          buildAndSetQueryFilter(_newFilters);
        }}
        menuClosed={() => {
          setSecondaryFilters(
            secondaryFilters.filter((o) => filters[o]?.length > 0),
          );
        }}
        customComponent={
          <FilterButton
            label={filter.label}
            values={
              filters[identifier]?.map(
                (o) => filterOptions?.find((f) => f.value === o)?.label || "",
              ) ?? []
            }
            clearValues={
              isDisabled
                ? undefined
                : () => {
                    const _newFilters = { ...filters };
                    delete _newFilters[identifier];
                    delete _newFilters[`${identifier}${filter?.gt_suffix}`];
                    delete _newFilters[`${identifier}${filter?.lt_suffix}`];
                    setSecondaryFilters(
                      secondaryFilters.filter((o) => o !== identifier),
                    );
                    buildAndSetQueryFilter(_newFilters);
                  }
            }
            disabled={isDisabled}
          />
        }
      />
    );
  };

  const renderNewFilter = () => {
    if (newFilter?.custom) {
      return (
        <CustomOptionsPopover
          opened
          data={{
            ...newFilter,
            identifier: `${newFilter.identifier}`
              .replaceAll(`${newFilter.gt_suffix}`, "")
              .replaceAll(`${newFilter.lt_suffix}`, ""),
          }}
          filters={filters}
          onChange={(newFilters) => {
            buildAndSetQueryFilter({ ...filters, ...newFilters });
          }}
          menuClosed={() => {
            const _secondaryFilters = secondaryFilters.filter(
              (o) => filters[o]?.length > 0,
            );

            if (
              newFilter?.identifier &&
              filters[newFilter.identifier]?.length > 0
            ) {
              _secondaryFilters.push(newFilter.identifier);
            }

            setSecondaryFilters(_secondaryFilters);
            setNewFilter(null);
          }}
        />
      );
    }

    const stripNullOptions = (
      opts: FilterOptionProps[],
      fullIdentifier: string,
    ): FilterOptionProps[] =>
      opts
        .filter((opt) =>
          opt.value === "__null__"
            ? showNullInFilters?.includes(fullIdentifier)
            : true,
        )
        .map((opt) =>
          opt.options?.length
            ? {
                ...opt,
                options: stripNullOptions(
                  opt.options,
                  `${fullIdentifier}.${opt.identifier ?? opt.value ?? ""}`,
                ),
              }
            : opt,
        );

    let filterOptions: FilterOptionProps[] = [];

    if (newFilter) {
      filterOptions = stripNullOptions(
        newFilter.options ?? [],
        newFilter.identifier ?? "",
      );
    } else {
      filterOptions = filterData
        .filter((o) => {
          const values = [
            o.value,
            `${o.value}${o?.gt_suffix ?? ""}`,
            `${o.value}${o?.lt_suffix ?? ""}`,
          ];

          return (
            !primaryFilters?.some((f) => values.includes(f)) &&
            !secondaryFilters.some((f) => values.includes(f))
          );
        })
        .map((o) => {
          const filteredOptions = o.options?.filter((f) => {
            const values = [
              `${o.value}.${f.value}`,
              `${o.value}.${f.value}${f?.gt_suffix ?? ""}`,
              `${o.value}.${f.value}${f?.lt_suffix ?? ""}`,
            ];

            return (
              !primaryFilters?.some((f) => values.includes(f)) &&
              !secondaryFilters.some((f) => values.includes(f))
            );
          });

          return {
            ...o,
            options: stripNullOptions(
              filteredOptions ?? [],
              o.identifier ?? o.value ?? "",
            ),
          };
        });
    }

    if (filterOptions.length === 0) {
      return null;
    }

    return (
      <Select
        multiValues
        checkbox
        selectedValuesAtTop
        showSelectAll={filterOptions.length <= 100}
        closeMenu={closeMenu}
        label={newFilter ? newFilter.label : "Filters"}
        type={newFilter ? "basic" : "breadcrumbs"}
        options={filterOptions as OptionProp[]}
        value={filters[`${newFilter?.identifier}`] ?? []}
        onChange={(e: string[], identifier?: string) => {
          if (identifier && !newFilter) {
            const foundFilter = findFilter(identifier, filterData);
            if (foundFilter) {
              setNewFilter({ ...foundFilter, identifier });
            }
          }

          if (!identifier && newFilter) {
            identifier = newFilter.identifier;
          }

          const _newFilters = { ...filters };
          if (identifier) {
            if (e.length === 0) {
              delete _newFilters[identifier];
            } else {
              _newFilters[identifier] = e;
            }
          }

          buildAndSetQueryFilter(_newFilters);
        }}
        menuClosed={() => {
          const _secondaryFilters = secondaryFilters.filter(
            (o) => filters[o]?.length > 0,
          );

          if (
            newFilter?.identifier &&
            filters[newFilter.identifier]?.length > 0
          ) {
            _secondaryFilters.push(newFilter.identifier);
          }

          setNewFilter(null);
          setSecondaryFilters(_secondaryFilters);
        }}
        customComponent={
          <FilterButton
            addButton={!newFilter}
            label={newFilter ? newFilter.label : "Add Filter"}
            values={
              (newFilter &&
                filters[newFilter.identifier ?? ""]?.map(
                  (o) =>
                    newFilter.options?.find((f) => f.value === o)?.label || "",
                )) ??
              []
            }
          />
        }
      />
    );
  };

  const layoutKey = tableLayout?.layoutKey;
  useEffect(() => {
    if (!usingSavedFilterName || !layoutKey) return;
    if (viewLayout.current === null) {
      viewLayout.current = layoutKey;
    } else if (viewLayout.current !== layoutKey) {
      setUsingSavedFilterName("");
      viewLayout.current = null;
    }
  }, [usingSavedFilterName, layoutKey]);

  const filtersActive = Object.keys(filters).some(
    (key) =>
      filters[key]?.length &&
      !hiddenFilters?.includes(key) &&
      !Object.keys(constantBaseFilters || {})?.includes(key),
  );

  // Readable filter names and option labels for a saved query, e.g. "Practice Status: Active"
  const describeQuery = useCallback(
    (query: string): FilterDescription[] =>
      describeQueryFallback(query, (key) => findFilter(key, filterData)),
    [filterData],
  );

  const renderViewButtons = () => {
    if (hideSavedFilters || loading || errors.view) return null;

    // Check if current filters exactly match baseFilters
    const onlyBaseFiltersActive = (() => {
      if (!baseFilters) return false;

      const activeFilterKeys = Object.keys(filters).filter(
        (key) =>
          filters[key]?.length &&
          !hiddenFilters?.includes(key) &&
          !Object.keys(constantBaseFilters || {})?.includes(key),
      );

      const baseFilterKeys = Object.keys(baseFilters);

      if (activeFilterKeys.length !== baseFilterKeys.length) return false;

      return baseFilterKeys.every((key) => {
        const baseValue = baseFilters[key as keyof typeof baseFilters];
        const filterValue = filters[key];

        return (
          filterValue &&
          filterValue.length === 1 &&
          filterValue[0] === baseValue
        );
      });
    })();

    const buttons = [];

    // Tables can save their layout, so there's something to save even without filters
    const showSaveButton =
      (filtersActive && !onlyBaseFiltersActive) || !!tableLayout;

    if (showSaveButton) {
      buttons.push(
        <Button
          mr
          key="save-view"
          text={
            usingSavedFilterName ? `View: ${usingSavedFilterName}` : "Save View"
          }
          variant="secondary"
          onClick={() => setFilterModalOpened("newFilter")}
          disabled={!!usingSavedFilterName}
          style={{
            marginBottom: gridSpacing[2],
          }}
        />,
      );
    }

    buttons.push(
      <Button
        key="all-views"
        text={showSaveButton ? undefined : "All Views"}
        variant="secondary"
        icon="fa-list"
        onClick={() => setFilterModalOpened(true)}
        style={{
          marginBottom: gridSpacing[2],
        }}
        disabled={savedFilters.length === 0}
        tooltip={
          savedFilters.length === 0
            ? "No saved views"
            : showSaveButton
              ? "All Views"
              : undefined
        }
      />,
    );

    return buttons;
  };

  return (
    <>
      <SaveFilterModal
        isOpened={filterModalOpened}
        close={() => setFilterModalOpened(false)}
        app={`${app}`}
        model={currentModel}
        pageKey={pageKey}
        query={buildQuery(removeConstantBaseFilters(filters), false)}
        filters={filters}
        constantFilters={[constantBaseFilters ?? {}]}
        savedFilters={savedFilters}
        openSavedFilter={({ name, query, slug, table_state }) => {
          if (table_state && tableLayout) tableLayout.apply(table_state, slug);
          setQueryInURL &&
            window.history.replaceState(
              null,
              "",
              `?filter=${slug}${window.location.hash}`,
            );
          const newFilters = buildFilters(addBaseFilters(query));
          setFilters(newFilters);
          setSecondaryFilters(
            Object.keys(newFilters).filter(
              (key) =>
                !primaryFilters?.includes(key) &&
                !hiddenFilters?.includes(key) &&
                !Object.keys(baseFilters || {})?.includes(key) &&
                !Object.keys(constantBaseFilters || {})?.includes(key),
            ),
          );

          if (newFilters.search?.[0]) {
            setPrimarySearchFilter(newFilters.search[0]);
          }

          const sendQuery = buildQuery(newFilters, true);
          const dotQuery = buildQuery(newFilters);
          setQuery(sendQuery, dotQuery);
          showView(name);
          setFilterModalOpened(false);
        }}
        saveFilter={(data) => {
          setSavedFilters([...savedFilters, data]);
          setFilterModalOpened(false);
          setQueryInURL &&
            window.history.replaceState(
              null,
              "",
              `?filter=${data.slug}${window.location.hash}`,
            );
          showView(data.name);
        }}
        removeFilter={(data) =>
          setSavedFilters(savedFilters.filter((o) => o.slug !== data.slug))
        }
        updateFilter={(data) =>
          setSavedFilters(
            savedFilters.map((o) => (o.uuid === data.uuid ? data : o)),
          )
        }
        tableLayout={tableLayout}
        describeQuery={describeQuery}
      />
      <div style={{ flex: 1 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap-reverse",
          }}
        >
          {!searchTextHidden && (
            <SearchText
              searchButton
              value={primarySearchFilter}
              onChange={(e) => {
                setPrimarySearchFilter(e.target.value);
                if (!e.target.value) {
                  const _filters = { ...filters };
                  delete _filters.search;
                  buildAndSetQueryFilter(_filters);
                }
              }}
              onEnter={() => {
                const _filters = { ...filters, search: [primarySearchFilter] };
                buildAndSetQueryFilter(_filters);
              }}
              style={{
                marginRight: gridSpacing[2],
                minWidth: searchTextWidth ?? 365,
                marginBottom: gridSpacing[2],
                width: searchTextWidth,
              }}
              placeholder={
                errors.main
                  ? "Error loading search"
                  : loading
                    ? "Loading Filters..."
                    : undefined
              }
              disabled={loading}
            />
          )}
          {!inlineViewButtons && renderViewButtons()}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {!errors.main ? (
            <FiltersContainer>
              {!loading && (
                <>
                  {primaryFilters?.map((id) => renderFilterSelect(id, true))}
                  {quickFilters?.map((filter) => (
                    <FilterButton
                      key={filter.name}
                      label={filter.name}
                      onClick={() => {
                        const _filters = { ...filters, ...filter.filters };
                        buildAndSetQueryFilter(_filters);
                      }}
                    />
                  ))}

                  {secondaryFilters?.map((id) => renderFilterSelect(id))}
                  {renderNewFilter()}
                  {filtersActive && (
                    <Button
                      small
                      style={{ padding: 0, margin: 0, minWidth: 0 }}
                      text="Clear all"
                      variant="neutralLink"
                      onClick={clearFilters}
                    />
                  )}
                </>
              )}
            </FiltersContainer>
          ) : (
            <div />
          )}
          {inlineViewButtons && renderViewButtons()}
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state: {
  main: { advancedFilters: { refresh: boolean } };
}) => {
  return {
    refresh: Boolean(state?.main?.advancedFilters?.refresh),
  };
};

export default connect(mapStateToProps, { setAdvancedFilterRefresh })(
  AdvancedFilters,
);
